import React, { useEffect } from "react";
import { EmailCyclesTable } from "../tables/EmailCyclesTable";
import { Grid, Stack } from "@mui/material";
import {
  LoadingIndicator,
  useFoundationColorTokens,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import {
  EmailCycleDeliveryStatsCard,
  EmailCycleDeliveryStatsCardProps,
} from "../cards/EmailCycleDeliveryStatsCard";
import {
  Download,
  MailCheck,
  MailOpen,
  MailWarning,
  MailX,
  Send,
  XCircle,
} from "lucide-react";
import { observer } from "mobx-react";
import {
  useDeliveryStatsForProjectStore,
  useDownloadStatsForProjectStore,
} from "../../stores/Hooks";
import { useTranslation } from "react-i18next";
import {
  processProjectParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { GetDeliveryStatsForProjectRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getDeliveryStatsForProjectRPC";
import { GetPresignedDownloadURLForStatsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getPresignedDownloadUrlForStatsRPC";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useProjectDetailsStore } from "../../../store/Hooks";

interface EmailCyclesTabProps {
  navigateToSurveyTab: VoidFunction;
}

const TABLE_MAX_WIDTH = "1376px";

export const EmailCyclesTab: React.FC<EmailCyclesTabProps> = observer(
  ({ navigateToSurveyTab }) => {
    const { t } = useTranslation();
    const navigateTo = useRouteNavigator();
    const spacing = useSpacing();
    const tokens = useFoundationColorTokens();

    const deliveryStatsForProjectStore = useDeliveryStatsForProjectStore();
    const downloadStatsForProjectStore = useDownloadStatsForProjectStore();
    const projectDetailsStore = useProjectDetailsStore();
    const downloadCSVText = t("common.downloadCSV");

    useEffect(() => {
      void processProjectParams(async (projectId) => {
        await deliveryStatsForProjectStore.getDeliveryStatsForProject(
          projectId,
        );
      }, navigateTo);
    }, []);

    useEffect(() => {
      if (!deliveryStatsForProjectStore.getDeliveryStatsForProjectRPCError) {
        return;
      }

      switch (deliveryStatsForProjectStore.getDeliveryStatsForProjectRPCError) {
        case GetDeliveryStatsForProjectRPC.RPCError.InvalidProjectId:
          navigateTo.pageNotFound();
          break;
        default:
          deliveryStatsForProjectStore.getDeliveryStatsForProjectRPCError satisfies never;
      }
    }, [deliveryStatsForProjectStore.getDeliveryStatsForProjectRPCError]);

    useEffect(() => {
      if (
        !downloadStatsForProjectStore.getPresignedDownloadURLForStatsRPCError
      ) {
        return;
      }

      switch (
        downloadStatsForProjectStore.getPresignedDownloadURLForStatsRPCError
      ) {
        case GetPresignedDownloadURLForStatsRPC.RPCError.InvalidProjectId:
          navigateTo.pageNotFound();
          break;
        default:
          downloadStatsForProjectStore.getPresignedDownloadURLForStatsRPCError satisfies never;
      }
    }, [downloadStatsForProjectStore.getPresignedDownloadURLForStatsRPCError]);

    useEffect(() => {
      return (): void => {
        deliveryStatsForProjectStore.resetStore();
        downloadStatsForProjectStore.resetStore();
      };
    }, []);

    const handleDownloadStats = (
      statType: GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType,
    ): void => {
      void processProjectParams(async (projectId) => {
        await downloadStatsForProjectStore.downloadStatsForProject(
          projectId,
          projectDetailsStore.projectName,
          statType,
        );
      }, navigateTo).catch(() =>
        downloadStatsForProjectStore.setHasUnexpectedDownloadError(true),
      );
    };

    const getDownloadProps = (
      statType: GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType,
      count: number,
      actionButtonName: string,
    ): Pick<
      EmailCycleDeliveryStatsCardProps,
      | "actionButtonName"
      | "actionIcon"
      | "actionLabel"
      | "onActionClick"
      | "isActionLoading"
      | "isActionDisabled"
    > => {
      const isActionLoading =
        downloadStatsForProjectStore.downloadingStatType === statType;
      const isActionDisabled =
        count === 0 ||
        (downloadStatsForProjectStore.downloadingStatType !== null &&
          !isActionLoading);

      return {
        actionButtonName,
        actionIcon: <Download />,
        actionLabel: downloadCSVText,
        onActionClick: () => handleDownloadStats(statType),
        isActionLoading,
        isActionDisabled,
      };
    };

    const bouncesAndComplaintsCount =
      deliveryStatsForProjectStore.deliveryStats.bounces +
      deliveryStatsForProjectStore.deliveryStats.complaints;

    const emailCycleDeliveryStatsCardPropList: EmailCycleDeliveryStatsCardProps[] =
      [
        {
          icon: <Send />,
          label: t("emailCycles.emailsSent"),
          count: deliveryStatsForProjectStore.deliveryStats.sent,
          color: tokens.backgroundPrimary,
          backgroundColor: tokens.backgroundPrimarySubtle,
        },
        {
          icon: <MailCheck />,
          label: t("emailCycles.delivered"),
          count: deliveryStatsForProjectStore.deliveryStats.delivered,
          color: tokens.backgroundInfo,
          backgroundColor: tokens.backgroundInfoSubtle,
        },
        {
          icon: <MailOpen />,
          label: t("surveyLinkStatus.viewed"),
          count: deliveryStatsForProjectStore.deliveryStats.viewed,
          color: tokens.backgroundSuccess,
          backgroundColor: tokens.backgroundSuccessSubtle,
        },
        {
          icon: <MailWarning />,
          label: t("emailCycles.unsent"),
          count: deliveryStatsForProjectStore.deliveryStats.unsent,
          color: tokens.labelSubtle,
          backgroundColor: tokens.backgroundSubtle,
          ...getDownloadProps(
            GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType
              .UNSENT,
            deliveryStatsForProjectStore.deliveryStats.unsent,
            "downloadUnsentStatsButton",
          ),
        },
        {
          icon: <XCircle />,
          label: t("emailCycles.optOuts"),
          count: deliveryStatsForProjectStore.deliveryStats.optOut,
          color: tokens.backgroundWarning,
          backgroundColor: tokens.backgroundWarningSubtle,
          ...getDownloadProps(
            GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType
              .OPT_OUT,
            deliveryStatsForProjectStore.deliveryStats.optOut,
            "downloadOptOutStatsButton",
          ),
        },
        {
          icon: <MailX />,
          label: t("emailCycles.bouncesAndComplaints"),
          count: bouncesAndComplaintsCount,
          color: tokens.backgroundError,
          backgroundColor: tokens.backgroundErrorSubtle,
          ...getDownloadProps(
            GetPresignedDownloadURLForStatsRPC.RequestEnums.StatType.StatType
              .BOUNCES_AND_COMPLAINTS,
            bouncesAndComplaintsCount,
            "downloadBouncesAndComplaintsStatsButton",
          ),
        },
      ];

    if (deliveryStatsForProjectStore.loadingDeliveryStats) {
      return <LoadingIndicator variant="overlay" isLoading />;
    }

    return (
      <Stack
        padding={spacing.spaceXL}
        height="100%"
        width="100%"
        alignItems="center"
      >
        <Stack width="100%" maxWidth={TABLE_MAX_WIDTH} gap={spacing.spaceXL}>
          <Grid container columns={6} width="100%" spacing={spacing.spaceSM}>
            {emailCycleDeliveryStatsCardPropList.map(
              (emailCycleDeliveryStatsCardProps, index) => (
                <Grid key={index} size={1}>
                  <EmailCycleDeliveryStatsCard
                    {...emailCycleDeliveryStatsCardProps}
                  />
                </Grid>
              ),
            )}
          </Grid>
          <EmailCyclesTable navigateToSurveyTab={navigateToSurveyTab} />
        </Stack>
        {downloadStatsForProjectStore.hasUnexpectedDownloadError && (
          <UnexpectedErrorDialog
            message={t("emailCycles.getEmailCyclesUnexpectedErrorMessage")}
            onClickDone={() =>
              downloadStatsForProjectStore.setHasUnexpectedDownloadError(false)
            }
          />
        )}
      </Stack>
    );
  },
);
